/**
 * useLeadHunter — Zustand + Abläufe der Seite Lead Hunter (Admin).
 *   Suche:   Bestand (Kontakte + offene Aufgaben), Ortsmittelpunkt und alle Suchbegriffe PARALLEL
 *            → Treffer sofort sichtbar → Kontaktdaten im Hintergrund.
 *   Konflikt: schon Kontakt / offene Agenten-Aufgabe → nicht zuweisbar (Liste „Bereits vorhanden“).
 *   Import:  frischer Bestand, Dubletten im Lauf, fehlende Details nachladen, Lead + Aufgabe
 *            „Besuchen“ je Agent, eine Benachrichtigung je Agent.
 * Alle Auswahl-/Zuweisungszustände sind nach google_place_id geschlüsselt (stabil beim Entfernen).
 */
import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { base44 } from '@/api/base44Client';
import { calculateLeadScore, categorizeByScore } from '@/components/utils/LeadCategorization';
import { findConflict, buildAgentStats, planAssignments, dedupeBatch } from '@/lib/leadAssignment';
import { searchPlaces, placeToLead, loadDetails, geocodeLocation, withinRadius, mapsCall } from '@/lib/leadHunter/places';
import { createNotification } from '@/components/notifications/notificationHelper';

export const MAX_NEW = 20;      // neue Treffer je Suche
const MAX_BLOCKED = 30;         // angezeigte, bereits vorhandene Treffer

async function loadBestand() {
  const [leads, tasks] = await Promise.all([
    base44.entities.Lead.list(),
    base44.entities.Task.filter({ status: 'Offen' }),
  ]);
  return { leads: leads || [], tasks: tasks || [] };
}

/** Treffer → neue Leads (bewertet) + bereits vorhandene (mit Konflikt) */
function splitPlaces(places, { branch, location, leads, tasks }) {
  const fresh = [];
  const blocked = [];
  for (const place of places) {
    const lead = placeToLead(place, branch, location);
    if (!lead.name) continue;
    const conflict = findConflict(lead, leads, tasks);
    if (conflict) {
      if (blocked.length < MAX_BLOCKED) blocked.push({ ...lead, conflict });
      continue;
    }
    if (fresh.length >= MAX_NEW) continue;
    const score = calculateLeadScore(lead, leads, [], []);
    fresh.push({ ...lead, lead_score: score, priority_category: categorizeByScore(score) });
  }
  return { fresh: fresh.sort((a, b) => b.lead_score - a.lead_score), blocked };
}

const notifyAgent = (agent, names) => createNotification({
  recipientEmail: agent,
  type: 'task_assigned',
  title: `${names.length} neue${names.length === 1 ? 'r Lead' : ' Leads'} zugewiesen`,
  message: `Lead Hunter: ${names.slice(0, 5).join(', ')}${names.length > 5 ? ` und ${names.length - 5} weitere` : ''}. Aufgabe „Besuchen“ ist angelegt.`,
  referenceType: 'task',
});

export function useLeadHunter() {
  const queryClient = useQueryClient();
  const [rule, setRule] = useState('gebiet');
  const [ruleAgents, setRuleAgents] = useState(null); // null = alle aktiven Agenten
  const [fixedAgent, setFixedAgent] = useState('');
  const [maxPerAgent, setMaxPerAgent] = useState('');
  const [results, setResults] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [assignments, setAssignments] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [detailProgress, setDetailProgress] = useState(null); // {done,total} während Kontaktdaten laden
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const searchId = useRef(0);
  const enriched = useRef(new Set());

  const { data: users = [] } = useQuery({ queryKey: ['users'], queryFn: () => base44.entities.User.list() });
  const { data: bestand = { leads: [], tasks: [] }, refetch: refetchBestand } = useQuery({
    queryKey: ['lead-hunter-bestand'], queryFn: loadBestand,
  });

  const agents = useMemo(() => users.filter((u) => {
    const r = (u.rolle || u.role || '').toLowerCase();
    return r === 'agent' && u.aktiv !== false && u.email;
  }), [users]);
  const stats = useMemo(() => buildAgentStats(agents, bestand.leads, bestand.tasks), [agents, bestand]);
  const activeRuleAgents = ruleAgents || new Set(agents.map((a) => a.email));

  const plan = (leads, statsOverride = stats) => {
    const emails = planAssignments(leads, {
      rule, agentEmails: [...activeRuleAgents], stats: statsOverride, fixedAgent,
      maxPerAgent: maxPerAgent ? Number(maxPerAgent) : null,
    });
    return Object.fromEntries(leads.map((l, k) => [l.google_place_id, emails[k] || '']));
  };

  const applyRule = () => {
    setAssignments((prev) => ({ ...prev, ...plan(results.filter((l) => selected.has(l.google_place_id))) }));
    toast.success('Zuweisungsvorschlag aktualisiert');
  };

  const enrichInBackground = (leads, id) => {
    const ids = leads.map((l) => l.google_place_id);
    if (!ids.length) return;
    setDetailProgress({ done: 0, total: ids.length });
    loadDetails(ids, {
      call: mapsCall,
      isCancelled: () => searchId.current !== id,
      onBatch: (patches, done) => {
        Object.keys(patches).forEach((pid) => enriched.current.add(pid));
        setResults((prev) => prev.map((l) => (patches[l.google_place_id] ? { ...l, ...patches[l.google_place_id] } : l)));
        setDetailProgress(done >= ids.length ? null : { done, total: ids.length });
      },
    }).catch(() => setDetailProgress(null));
  };

  const search = async ({ branch, location, radiusKm }) => {
    if (!branch || !location.trim()) { setError('Bitte Branche und Ort wählen.'); return; }
    const id = ++searchId.current;
    enriched.current = new Set();
    setIsSearching(true);
    setError(null); setWarning(null); setImportResult(null);
    setResults([]); setBlocked([]); setSelected(new Set()); setAssignments({}); setDetailProgress(null);
    const errors = [];
    try {
      const [bestandRes, center, places] = await Promise.all([
        refetchBestand(),
        geocodeLocation(location, mapsCall),
        searchPlaces({ branch, location, call: mapsCall, errors }),
      ]);
      if (searchId.current !== id) return;
      const bestandOk = !bestandRes.error && bestandRes.data;
      const { leads, tasks } = bestandOk ? bestandRes.data : { leads: [], tasks: [] };
      if (!bestandOk) errors.unshift('Kontakt-Liste nicht ladbar – Dubletten möglich');
      const { fresh, blocked: hit } = splitPlaces(withinRadius(places, center, radiusKm), { branch, location, leads, tasks });

      setResults(fresh);
      setBlocked(hit);
      setSelected(new Set(fresh.map((l) => l.google_place_id)));
      setAssignments(plan(fresh, buildAgentStats(agents, leads, tasks)));
      if (errors.length) setWarning(`Suchwarnungen: ${errors.slice(0, 3).join(' | ')}${errors.length > 3 ? ` (+${errors.length - 3})` : ''}`);
      if (!fresh.length && !hit.length && !errors.length) setWarning('Keine Treffer im gewählten Umkreis.');
      enrichInBackground(fresh, id);
    } catch (err) {
      setError(`Fehler bei der Suche: ${err.message}`);
    } finally {
      if (searchId.current === id) setIsSearching(false);
    }
  };

  /** Ausgewählte Leads ohne geladene Kontaktdaten vor dem Anlegen ergänzen */
  const withDetails = async (leads) => {
    const missing = leads.filter((l) => !enriched.current.has(l.google_place_id)).map((l) => l.google_place_id);
    if (!missing.length) return leads;
    const patches = await loadDetails(missing, { call: mapsCall });
    return leads.map((l) => ({ ...l, ...(patches[l.google_place_id] || {}) }));
  };

  const createLeadWithTask = async (lead, agent, today) => {
    const { lead_score, priority_category, ...data } = lead;
    const created = await base44.entities.Lead.create({ ...data, lead_score, priority_category, assigned_agent: agent });
    await base44.entities.Task.create({
      lead_id: created.id,
      lead_name: created.name,
      lead_address: [created.strasse, created.plz, created.ort].filter(Boolean).join(', '),
      lead_opening_hours: lead.opening_hours || null,
      task_type: 'Besuchen', typ: 'Besuchen',
      due_date: today, status: 'Offen',
      info_block: 'Erstkontakt herstellen (Lead Hunter)',
      assigned_agent: agent,
    });
    return created;
  };

  const importSelected = async () => {
    if (!selected.size || importing) return;
    setImporting(true);
    setError(null);
    const result = { imported: 0, conflicts: [], noAgent: 0, failed: [] };
    const perAgent = new Map();
    const done = new Set();
    try {
      const fresh = await refetchBestand();
      if (fresh.error || !fresh.data) throw new Error(`Bestand nicht ladbar: ${fresh.error?.message || 'unbekannt'}`);
      const latestLeads = [...fresh.data.leads]; // Kopie: wird im Lauf ergänzt
      const chosen = await withDetails(results.filter((l) => selected.has(l.google_place_id)));
      const batch = dedupeBatch(chosen);
      const today = new Date().toISOString().split('T')[0];

      for (const { lead, kept } of batch) {
        if (!kept) { result.conflicts.push({ name: lead.name, text: 'doppelt in der Auswahl' }); continue; }
        const conflict = findConflict(lead, latestLeads, fresh.data.tasks);
        if (conflict) {
          result.conflicts.push({ name: lead.name, text: conflict.agent ? `bereits bei ${conflict.agent}` : 'bereits als Kontakt vorhanden' });
          continue;
        }
        const agent = assignments[lead.google_place_id];
        if (!agent) { result.noAgent += 1; continue; }
        try {
          const created = await createLeadWithTask(lead, agent, today);
          latestLeads.push(created);
          perAgent.set(agent, [...(perAgent.get(agent) || []), created.name]);
          done.add(lead.google_place_id);
          result.imported += 1;
        } catch (e) {
          result.failed.push({ name: lead.name, text: e.message });
        }
      }
      await Promise.all([...perAgent.entries()].map(([agent, names]) => notifyAgent(agent, names)));
    } catch (err) {
      setError(`Fehler beim Import: ${err.message}`);
    } finally {
      setResults((prev) => prev.filter((l) => !done.has(l.google_place_id)));
      setSelected(new Set());
      setImportResult(result);
      setImporting(false);
      queryClient.invalidateQueries(['admin-leads']);
      queryClient.invalidateQueries(['admin-all-tasks']);
      refetchBestand();
    }
  };

  const toggle = (pid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    return next;
  });
  const toggleAll = () => setSelected(selected.size === results.length ? new Set() : new Set(results.map((l) => l.google_place_id)));
  const setAgent = (pid, email) => setAssignments((prev) => ({ ...prev, [pid]: email }));

  return {
    agents, stats,
    rules: { rule, setRule, ruleAgents: activeRuleAgents, setRuleAgents, fixedAgent, setFixedAgent, maxPerAgent, setMaxPerAgent, applyRule },
    results, blocked, selected, toggle, toggleAll, assignments, setAgent,
    search, isSearching, detailProgress,
    importSelected, importing, importResult, clearImportResult: () => setImportResult(null),
    error, warning,
  };
}
